<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'unemployedNewAndActiveList' }"
          marginBottom="5"/>

      <h6 class="element-header">Kundendetails</h6>

      <div class="element-box">
        <UnemployedDetails :unemployed="unemployed"/>
        <CoachingMenu :unemployed="unemployed" :coachingCounts="coachingCounts"/>

        <div class="row mb-2 mt-4" style="padding: 0 10px">
          <div class="col-lg-6">
            <h6>Private Weiterbildung</h6>
          </div>
          <div class="col-lg-6 text-end">
            <button type="button" class="btn btn-primary mb-2 me-2"
                    style="padding: 5px 7px;"
                    @click="handleModalForm">
              Neue Maßnahme anlegen
            </button>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-4">
          <table id="dataTable1" class="table table-striped">
            <thead>
            <tr>
              <th>Status</th>
              <th>Maßnahmenart</th>
              <th>JC/AA Nr</th>
              <th>Standort</th>
              <th>Start</th>
              <th>Ende</th>
              <th>Stand</th>
              <th>Stand %</th>
              <th>Letzter Termin</th>
              <th>Bericht</th>
              <th>Bearbeiten</th>
              <th v-if="isAdmin">Historie</th>
              <th>Löschen</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="coaching in privateLearningCoachingList" :key="coaching.id">
              <td>
                <StatusBadge :status="coaching?.status" :label="coaching?.statusTranslation"/>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching?.course?.operationTypeTranslation }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching?.unemployed?.aaNumber }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching?.unemployed?.location?.name }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching?.startDate }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching?.endDate }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching?.unitsDone }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching?.unitsDonePercent }} %
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching?.lastUnit }}
                </strong>
              </td>
              <td>
                <ReportButton
                    :to="{ name: 'privateLearningCoachingProtocolList', params: { id: coaching.id } }"/>
              </td>
              <td>
                <EditButton @click="handleEditModalForm(coaching.id)"/>
              </td>
              <td v-if="isAdmin">
                <HistoryButton
                    @click="downloadFile(`/unemployed/private-learning-coaching/history-pdf/${coaching.id}`, undefined)"/>
              </td>
              <!-- Delete -->
              <td class="text-center">
                <DeleteButton @click="handleDeleteModal(coaching?.id)"/>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Modal
          v-model="showModalForm"
          title="Private Weiterbildung"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-lg"
          :hide-footer="true"
          classFooter="justify-content-center">
        <Form
            :privateLearningCoachingId="privateLearningCoachingId"
            :unemployedId="unemployed?.id"
            :locationId="unemployed?.location?.id"
            :locationName="unemployed?.location?.name"
            v-model:submitForm="submitForm"/>
      </Modal>

      <ModalDelete
          v-model="showDeleteModal"
          :header="t('general.delete_item_header')"
          :onDelete="deletePrivateLearningCoaching">
      </ModalDelete>
    </div>
  </PageTemplate>
</template>

<script setup>
import unemployedService from '@/api/Unemployed';
import privateLearningCoachingService from '@/api/Unemployed/PrivateLearningCoaching';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import CoachingMenu from '@/views/App/Unemployed/Coaching/CoachingMenu.vue';
import Form from '@/views/App/Unemployed/Coaching/PrivateLearningCoaching/Form.vue';
import UnemployedDetails from '@/views/App/Unemployed/UnemployedDetails.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";
import ReportButton from "@/components/button/ReportButton.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import EditButton from "@/components/button/EditButton.vue";
import DeleteButton from "@/components/button/DeleteButton.vue";
import HistoryButton from "@/components/button/HistoryButton.vue";
import {piniaUserStore} from "@/store/piniaUserStore";
import {UserRole} from "@/shared/enums/UserRolesEnums";
import {downloadFile} from "@/shared/functions";

const piniaUserState = piniaUserStore();
const isAdmin        = computed(() => piniaUserState.currentUser?.user?.roles?.[0] === UserRole.ADMIN);

const { t }                       = useI18n();
const route                       = useRoute();
const unemployedId                = route.params.id;
const unemployed                  = ref(null);
const coachingCounts              = ref(null);
const privateLearningCoachingList = ref([]);
const privateLearningCoachingId   = ref(null);
const isLoaded                    = ref(false);
const showModalForm               = ref(false);
const showDeleteModal             = ref(false);
const submitForm                  = ref(false);

async function getUnemployedOverviewData() {
  try {
    const { data }       = await unemployedService.getUnemployedOverview(unemployedId);
    unemployed.value     = data.data.unemployed;
    coachingCounts.value = data.data.coachingCounts;

  } catch (error) {
    await ErrorToast(error);
  }
}

async function getPrivateLearningCoachingList() {
  try {
    const { data } = await privateLearningCoachingService.getPrivateLearningCoachingList(
        unemployedId,
    );

    privateLearningCoachingList.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function deletePrivateLearningCoaching() {
  try {
    await privateLearningCoachingService.deletePrivateLearningCoaching(
        privateLearningCoachingId.value,
    );

    privateLearningCoachingId.value = null;
    showDeleteModal.value           = false;

    await getUnemployedOverviewData();
    await getPrivateLearningCoachingList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

watch(submitForm, () => {
  privateLearningCoachingId.value = null; // Import
  showModalForm.value             = false;
  showDeleteModal.value           = false;
  getUnemployedOverviewData();
  getPrivateLearningCoachingList();
});

function handleModalForm() {
  CloseToast();
  privateLearningCoachingId.value = null; // Important
  showModalForm.value             = true;
  showDeleteModal.value           = false;
}

function handleDeleteModal(id) {
  CloseToast();
  privateLearningCoachingId.value = id;
  showDeleteModal.value           = true;
  showModalForm.value             = false;
}

function handleEditModalForm(id) {
  CloseToast();
  privateLearningCoachingId.value = id;
  showModalForm.value             = true;
  showDeleteModal.value           = false;
}

onMounted(async () => {
  CloseToast();

  await Promise.all([
    getUnemployedOverviewData(),
    getPrivateLearningCoachingList(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>